'use client';

import { useState } from 'react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { ContractorCard, type ContractorCardProps } from '@/src/entities/contractor/ui/ContractorCard';
import { cn } from '@/src/shared/lib/utils';

type SortOption = 'all' | 'rating' | 'price-low' | 'price-high' | 'tasks';

interface ContractorsListProps {
  contractors: ContractorCardProps[];
  categoryName?: string;
  subcategoryName?: string;
  initialDisplayCount?: number;
  onLocationClick?: (id: string) => void;
  className?: string;
}

export const ContractorsList = ({
  contractors,
  categoryName,
  subcategoryName,
  initialDisplayCount = 8,
  onLocationClick,
  className,
}: ContractorsListProps) => {
  const [displayCount, setDisplayCount] = useState(initialDisplayCount);
  const [sortBy, setSortBy] = useState<SortOption>('all');

  // Sort contractors based on selected option
  const sortedContractors = [...contractors].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'price-low':
        return (a.hourlyRate || 0) - (b.hourlyRate || 0);
      case 'price-high':
        return (b.hourlyRate || 0) - (a.hourlyRate || 0);
      case 'tasks':
        return b.completedOrders - a.completedOrders;
      default:
        return 0;
    }
  });

  const displayedContractors = sortedContractors.slice(0, displayCount);
  const hasMore = displayCount < contractors.length;

  const handleLoadMore = () => {
    setDisplayCount((prev) => Math.min(prev + 8, contractors.length));
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Header with Sort */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <Typography variant="h2" weight="bold" color="primary">
          Contractors
        </Typography>

        {/* Sort Dropdown */}
        <div className="flex items-center gap-2">
          <Typography variant="body" color="secondary">
            Sort by:
          </Typography>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className={cn(
              'px-4 py-2 rounded-full border-2 border-border-primary',
              'bg-background-primary text-text-primary',
              'focus:outline-none focus:border-accent-primary',
              'cursor-pointer transition-colors'
            )}
          >
            <option value="all">all</option>
            <option value="rating">Highest rating</option>
            <option value="price-low">Lowest price</option>
            <option value="price-high">Highest price</option>
            <option value="tasks">Most tasks</option>
          </select>
        </div>
      </div>

      {/* Contractors Grid */}
      {displayedContractors.length > 0 ? (
        <div className="grid-cards mb-6">
          {displayedContractors.map((contractor) => (
            <ContractorCard
              key={contractor.id}
              {...contractor}
              onLocationClick={onLocationClick}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Typography variant="h3" color="secondary">
            No contractors found
          </Typography>
          <Typography variant="body" color="secondary" className="mt-2">
            Try adjusting your filters
          </Typography>
        </div>
      )}

      {/* Load More Button */}
      {hasMore && (
        <div className="bg-surface-highlight py-4 rounded-[16px] flex items-center justify-center">
          <button
            onClick={handleLoadMore}
            className={cn(
              'flex items-center gap-2 text-accent-primary font-semibold',
              'hover:text-accent-secondary transition-colors',
              'focus:outline-none focus:underline'
            )}
          >
            <span>More</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="transition-transform group-hover:translate-y-0.5"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
